import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FaPen } from "react-icons/fa";
import { Toaster } from "react-hot-toast";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";

const VERIFIED_FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "Verified", value: "1" },
  { label: "Not Verified", value: "0" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const renderVerificationBadge = (value) => {
  const isVerified = Number(value) === 1;

  return (
    <span
      className={`inline-flex min-w-[92px] justify-center rounded-full px-3 py-1 text-xs font-medium ${
        isVerified ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {isVerified ? "Verified" : "Not Verified"}
    </span>
  );
};

const ChannelPartnerList = () => {
  const navigate = useNavigate();

  const today = new Date().toISOString().split("T")[0];
  const firstDayOfMonth = `${today.slice(0, 8)}01`;

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [mobileVerified, setMobileVerified] = useState("");
  const [emailVerified, setEmailVerified] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const fetchChannelPartners = async () => {
    setLoading(true);
    setLoadError("");

    const params = {
      page: String(currentPage),
      limit: String(recordsPerPage),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(status ? { status } : {}),
      ...(mobileVerified !== "" ? { mobile_verified: mobileVerified } : {}),
      ...(emailVerified !== "" ? { email_verified: emailVerified } : {}),
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    };

    const res = await apiCall({
      endpoint: "admin/channel-partner/list",
      method: "GET",
      params,
    });

    if (res?.status === 200) {
      setData(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(res.pagination?.current_page || currentPage);
      setTotalRecords(res.pagination?.total_records || 0);
      setTotalPages(res.pagination?.total_pages || 0);
      setLoading(false);
      return;
    }

    setData([]);
    setTotalRecords(0);
    setTotalPages(0);
    setLoadError(res?.message || "Unable to load channel partners.");
    setLoading(false);
  };

  useEffect(() => {
    fetchChannelPartners();
  }, [currentPage, recordsPerPage, debouncedSearch, status, mobileVerified, emailVerified, startDate, endDate]);

  const emptyStateMessage = useMemo(() => {
    if (loading) {
      return "Loading channel partners...";
    }

    if (loadError) {
      return loadError;
    }

    return "No channel partners found.";
  }, [loadError, loading]);

  const handleFilterChange = (setter) => (event) => {
    setCurrentPage(1);
    setter(event.target.value);
  };

  const applyExampleFilters = () => {
    setCurrentPage(1);
    setSearchTerm("rajesh");
    setDebouncedSearch("rajesh");
    setStatus("active");
    setMobileVerified("1");
    setEmailVerified("0");
    setStartDate(firstDayOfMonth);
    setEndDate(today);
  };

  const clearFilters = () => {
    setCurrentPage(1);
    setSearchTerm("");
    setDebouncedSearch("");
    setStatus("");
    setMobileVerified("");
    setEmailVerified("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="border-solid border-2 mt-6 bg-white rounded-lg shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-8 pt-6">
          <div className="text-lg font-semibold">Channel Partner</div>
          <div className="flex items-center gap-3">
            <button
              onClick={applyExampleFilters}
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              Apply Sample Filters
            </button>
            <button
              onClick={clearFilters}
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 px-8 py-4 md:grid-cols-2 xl:grid-cols-6">
          <input
            type="text"
            placeholder="Search name, mobile, email..."
            value={searchTerm}
            onChange={(event) => {
              setCurrentPage(1);
              setSearchTerm(event.target.value);
            }}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          />

          <select
            value={status}
            onChange={handleFilterChange(setStatus)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all-status"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={mobileVerified}
            onChange={handleFilterChange(setMobileVerified)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          >
            {VERIFIED_FILTER_OPTIONS.map((option) => (
              <option key={`mobile-${option.value || "all"}`} value={option.value}>
                {`Mobile ${option.label}`}
              </option>
            ))}
          </select>

          <select
            value={emailVerified}
            onChange={handleFilterChange(setEmailVerified)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          >
            {VERIFIED_FILTER_OPTIONS.map((option) => (
              <option key={`email-${option.value || "all"}`} value={option.value}>
                {`Email ${option.label}`}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={handleFilterChange(setStartDate)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          />

          <input
            type="date"
            value={endDate}
            onChange={handleFilterChange(setEndDate)}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg p-2.5"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border rounded-md">
            <thead className="border-b bg-gray-100 font-medium text-gray-700">
              <tr>
                <th className="px-4 py-3 text-center">Sr.No</th>
                <th className="px-4 py-3 text-center">Name</th>
                <th className="px-4 py-3 text-center">Company</th>
                <th className="px-4 py-3 text-center">Email</th>
                <th className="px-4 py-3 text-center">Mobile</th>
                <th className="px-4 py-3 text-center">Email Verified</th>
                <th className="px-4 py-3 text-center">Mobile Verified</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Last Login</th>
                <th className="px-4 py-3 text-center">Created At</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="text-center text-gray-800">
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td
                      className="px-4 py-3 text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        navigate("/channel-partner-details", {
                          state: { partner: item },
                        })
                      }
                    >
                      {item.name || "-"}
                    </td>
                    <td className="px-4 py-3">{item.company_name || "-"}</td>
                    <td className="px-4 py-3">{item.email || "-"}</td>
                    <td className="px-4 py-3">{item.mobile || "-"}</td>
                    <td className="px-4 py-3">{renderVerificationBadge(item.email_verified)}</td>
                    <td className="px-4 py-3">{renderVerificationBadge(item.mobile_verified)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex min-w-[80px] justify-center rounded-full px-3 py-1 text-xs font-medium ${
                          item.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {item.status || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{formatDateTime(item.last_login_at)}</td>
                    <td className="px-4 py-3">{formatDateTime(item.created_at)}</td>
                    <td className="px-4 py-3">
                      <FaPen
                        className="text-green-600 hover:text-green-800 cursor-pointer mx-auto"
                        size={18}
                        onClick={() =>
                          navigate("/channel-partner-edit", {
                            state: { editChannelPartner: item },
                          })
                        }
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-sm text-gray-500">
                    {emptyStateMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={(val) => {
              setCurrentPage(1);
              setRecordsPerPage(val);
            }}
            totalRecords={totalRecords}
            count={data.length}
            showFirstLastControls
            compact
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChannelPartnerList;
